import { forwardRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  MdOutlineSubscriptions,
  MdSystemUpdateAlt,
  MdCampaign,
} from "react-icons/md";
import { FaBlog, FaRocket } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import { LuUsers } from "react-icons/lu";
import { MdOutlineDashboard } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";
import { HiUsers } from "react-icons/hi2";
import { FiX } from "react-icons/fi";

const Sidebar = forwardRef(({ onClose }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Initialize on client side
  useEffect(() => {
    setIsClient(true);
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 770);
    };
    
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Get user role from localStorage
  const userRole = localStorage.getItem("adminUserRole") || "Admin";

  // Define which tabs each role can see
  const canSeeTab = (tabName) => {
    if (userRole === "Admin") {
      return true;
    }
    if (userRole === "Author") {
      return [
        "Dashboard",
        "Notifications",
        "Blog",
        "Promotional Messages",
      ].includes(tabName);
    }
    if (userRole === "CSR") {
      return [
        "Dashboard",
        "Users",
        "Subscription",
        "Match User",
        "User Profiles",
      ].includes(tabName);
    }
    return false;
  };

  const handleNavigation = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
    if (isMobile && onClose) {
      onClose();
    }
  };

  const SidebarContent = () => (
    <div
      ref={ref}
      className={`lg:ml-8 px-2 ml-2 lg:px-0 w-full md:w-auto lg:w-80 h-full min-h-[calc(100vh-120px)] bg-black rounded-xl shadow-xl overflow-auto no-scrollbar text-white`}
    >
      {/* Close button for mobile */}
      {isMobile && onClose && (
        <div className="flex justify-end p-4">
          <button
            onClick={() => onClose()}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      )}

      <div className="flex flex-col mt-6 md:mt-10 gap-1 md:gap-0 justify-start">
        {canSeeTab("Dashboard") && (
          <div
            onClick={() => handleNavigation("/dashboard")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                            cursor-pointer mb-1 flex items-center gap-3
                            ${
                              location.pathname === "/" || location.pathname === "/dashboard"
                                ? "bg-white text-black rounded-full"
                                : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                            }`}
            >
              <div className="md:mr-3">
                <MdOutlineDashboard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Dashboard</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-1 gap-1">
        {canSeeTab("Users") && (
          <div
            onClick={() => handleNavigation("/topusers")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "topusers"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <CiUser className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Users</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Subscription") && (
          <div
            onClick={() => handleNavigation("/subscription")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "subscription"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <MdOutlineSubscriptions className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Subscription</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Notifications") && (
          <div
            onClick={() => handleNavigation("/notifications")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "notifications"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <IoMdNotificationsOutline className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Notifications</p>
              </div>
            </div>
          </div>
        )}
        {canSeeTab("Promotional Messages") && (
          <div
            onClick={() => handleNavigation("/promotional-messages")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "promotional-messages"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <MdCampaign className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Promotional Messages</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Blog") && (
          <div
            onClick={() => handleNavigation("/blogs")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes("blogs")
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <FaBlog className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Blog</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("User Profiles") && (
          <div
            onClick={() => handleNavigation("/user-profiles")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/user-profiles"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <LuUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">User Profiles</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Match User") && (
          <div
            onClick={() => handleNavigation("/match-user")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/match-user"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <FaUserFriends className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Match User</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("App Version Control") && (
          <div
            onClick={() => handleNavigation("/app-version")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/app-version"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <MdSystemUpdateAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">App Version Control</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Admin Panel Users") && (
          <div
            onClick={() => handleNavigation("/admin-panel-users")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/admin-panel-users"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <HiUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Admin Panel Users</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Boost History") && (
          <div
            onClick={() => handleNavigation("/boost-history")}
          >
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/boost-history"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}
            >
              <div className="md:mr-3">
                <FaRocket className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Boost History</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );





  // Don't render anything until we know if we're on client side
  if (!isClient) {
    return null;
  }

  return <SidebarContent />;
});

export default Sidebar;